import React, { useEffect, useRef, useState } from 'react';
import { Table, Tag, Select, Button, DatePicker, Modal, Descriptions, message, Space, Input } from 'antd';
import request from '../utils/request';
import { fmtTime } from '../utils/format';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

const statusMap: Record<string, { color: string; text: string }> = {
  PENDING: { color: 'blue', text: '排队中' },
  CALLING: { color: 'gold', text: '就诊中' },
  COMPLETED: { color: 'green', text: '已完成' },
  CANCELLED: { color: 'red', text: '已取消' },
  ABSENT: { color: 'orange', text: '已过号' },
  EXPIRED: { color: 'default', text: '已过期' },
};

// 挂号记录：历史查询（日期范围 + 状态筛选），叫号操作请前往"今日挂号"
const Registrations: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  // 姓名模糊搜索：输入为暂存态，点查询/回车才提交，避免逐字符打接口
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [detail, setDetail] = useState<any>(null);
  const reqSeq = useRef(0);

  const fetchList = async (p = page) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const params: any = { page: p, pageSize: 10 };
      if (status) params.status = status;
      if (keyword) params.keyword = keyword;
      if (range?.[0]) params.startDate = range[0].format('YYYY-MM-DD');
      if (range?.[1]) params.endDate = range[1].format('YYYY-MM-DD');
      const res: any = await request.get('/registration', { params });
      if (seq !== reqSeq.current) return;
      setList(res.list);
      setTotal(res.total);
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page, status, range, keyword]);

  const doSearch = () => {
    setPage(1);
    setKeyword(kwInput.trim());
  };

  const hasFilters = !!(keyword || status || range);
  const resetFilters = () => {
    setPage(1);
    setKwInput('');
    setKeyword('');
    setStatus(undefined);
    setRange(null);
  };

  const showDetail = async (id: number) => {
    const res: any = await request.get(`/registration/${id}/detail`);
    setDetail(res);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '排队号', dataIndex: 'queueNo', width: 90, render: (v: number) => `${v} 号` },
    { title: '患者', dataIndex: 'patientName' },
    { title: '电话', dataIndex: 'patientPhone', render: (v: string) => v || '-' },
    { title: '挂号日期', dataIndex: 'regDate', render: (v: string) => v?.split('T')[0] },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (v: string) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag>,
    },
    { title: '挂号时间', dataIndex: 'createdAt', render: (v: string) => fmtTime(v) },
    {
      title: '操作', width: 90, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Button size="small" onClick={() => showDetail(record.id)}>详情</Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>挂号记录</h2>
        <Space wrap>
          <Input
            placeholder="患者姓名"
            allowClear
            style={{ width: 160 }}
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onPressEnter={doSearch}
          />
          <RangePicker
            value={range as any}
            onChange={(v) => { setRange(v as any); setPage(1); }}
            allowClear
            disabledDate={(d) => d.isAfter(dayjs().endOf('day'))}
          />
          <Select allowClear placeholder="筛选状态" style={{ width: 140 }} value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={[
              { label: '排队中', value: 'PENDING' },
              { label: '就诊中', value: 'CALLING' },
              { label: '已完成', value: 'COMPLETED' },
              { label: '已取消', value: 'CANCELLED' },
              { label: '已过号', value: 'ABSENT' },
              { label: '已过期', value: 'EXPIRED' },
            ]} />
          <Button type="primary" onClick={doSearch}>查询</Button>
          {hasFilters && <Button onClick={resetFilters}>重置</Button>}
        </Space>
      </div>

      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1000 }}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage, showTotal: (t) => `共 ${t} 条` }} />

      <Modal title="挂号详情" open={!!detail} footer={null} onCancel={() => setDetail(null)} width={640}>
        {detail && (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="ID">{detail.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[detail.status]?.color}>{statusMap[detail.status]?.text}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="排队号">{detail.queueNo} 号</Descriptions.Item>
            <Descriptions.Item label="挂号日期">{detail.regDate?.split('T')[0]}</Descriptions.Item>
            <Descriptions.Item label="患者">{detail.patientName}</Descriptions.Item>
            <Descriptions.Item label="电话">{detail.patientPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="身份证号" span={2}>{detail.idCard || '-'}</Descriptions.Item>
            <Descriptions.Item label="用户ID">{detail.userId}</Descriptions.Item>
            <Descriptions.Item label="挂号时间">{fmtTime(detail.createdAt, true)}</Descriptions.Item>
            <Descriptions.Item label="挂号诉求" span={2}>{detail.demand || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Registrations;
